// src/pages/AlarmDetails/tabs/LocationTab.jsx
// Location tab showing detailed location information with flexible display for fixed/mobile buttons

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as PreciseLocationIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BuildingIcon,
  MeetingRoom as RoomIcon,
  Router as AntennaIcon,
  SignalCellularAlt as SignalIcon,
  Apartment as SiteIcon,
  Map as MapIcon,
  GpsFixed as GpsIcon,
  Person as PersonIcon,
  DeviceHub as DeviceIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Navigation as NavigationIcon,
  Layers as FloorIcon
} from '@mui/icons-material';
import { MapContainer, ImageOverlay, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { locationsApi } from '../../../services/apiClient';
import indoorMappingService from '../../../services/indoorMappingService';
import AlarmNavigationPanel from '../../../components/navigation/AlarmNavigationPanel';
import { useIsMobile } from '../../../hooks/useResponsive';

// Location type icons
const getLocationTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'room':
    case 'apartment':
    case 'unit':
      return <RoomIcon />;
    case 'building':
    case 'floor':
      return <BuildingIcon />;
    case 'site':
    case 'campus':
      return <SiteIcon />;
    default:
      return <LocationIcon />;
  }
};

// Custom alarm marker icon
const alarmIcon = new L.DivIcon({
  className: 'alarm-marker',
  html: `<div style="
    width: 30px;
    height: 30px;
    background: #f44336;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: pulse 1.5s infinite;
  ">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Get color for POI type
const getPOIColor = (poi) => {
  const typeCode = poi?.poiType?.code?.toLowerCase() || poi?.type?.toLowerCase() || '';
  const typeName = poi?.poiType?.name?.toLowerCase() || '';

  const colorMap = {
    entrance: '#4CAF50',
    exit: '#F44336',
    stairs: '#607D8B',
    elevator: '#009688',
    restroom: '#9C27B0',
    restaurant: '#FF9800',
    medical: '#E91E63',
    aed: '#E91E63',
    office: '#2196F3',
  };

  // Check by code first, then by name
  if (colorMap[typeCode]) return colorMap[typeCode];

  // Check if name contains key words
  for (const [key, color] of Object.entries(colorMap)) {
    if (typeName.includes(key)) return color;
  }

  return '#2196F3'; // Default blue
};

const LocationTab = ({ alarm, isPublicAccess = false }) => {
  const isMobile = useIsMobile();
  const [locationDetails, setLocationDetails] = useState(null);
  const [locationHierarchy, setLocationHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);

  // Floor plan / map state
  const [floorPlans, setFloorPlans] = useState([]);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [loadingFloorPlans, setLoadingFloorPlans] = useState(false);
  const [linkedWayfindingNodeId, setLinkedWayfindingNodeId] = useState(null); // Node ID found during floor plan loading
  const [buildingPois, setBuildingPois] = useState([]); // All POIs for the building
  const [loadingPois, setLoadingPois] = useState(false);

  // Determine button type using priority-based logic
  const getButtonType = () => {
    // Priority 1: Check Content (most reliable - from original event processing)
    if (alarm?.Content?.buttonType) {
      return alarm.Content.buttonType;
    }

    // Priority 2: Check buttonDeviceModel config
    if (alarm?.buttonDeviceModel?.config?.buttonType) {
      return alarm.buttonDeviceModel.config.buttonType;
    }

    // Priority 3: Check deviceModel config
    if (alarm?.deviceModel?.config?.buttonType) {
      return alarm.deviceModel.config.buttonType;
    }

    // Priority 4: Infer from associations
    // If has person -> likely mobile
    if (alarm?.deviceModel?.devicePerson || alarm?.personModel?.id || alarm?.Content?.personId) {
      return 'mobile';
    }

    // If has location but no person -> likely fixed
    if (alarm?.deviceModel?.deviceLocation || alarm?.buttonDeviceModel?.deviceLocation) {
      return 'fixed';
    }

    return 'unknown';
  };

  const buttonType = getButtonType();

  // Fixed button: explicitly set as fixed OR has location but no person
  const isFixedButton = buttonType === 'emergencyFixed' ||
                        buttonType === 'fixed' ||
                        alarm?.Content?.locationSource === 'database' ||
                        (alarm?.deviceModel?.locationModel && !alarm?.deviceModel?.devicePerson && !alarm?.personModel?.id);

  // Mobile button: explicitly set as mobile OR has person associated
  const isMobileButton = buttonType === 'emergencyMobile' ||
                         buttonType === 'mobile' ||
                         alarm?.Content?.locationSource === 'person' ||
                         alarm?.personModel?.id ||
                         alarm?.Content?.personId;

  // Load location details
  useEffect(() => {
    const loadLocationDetails = async () => {
      if (!alarm?.Location && !alarm?.locationModel) {
        setLoading(false);
        return;
      }

      try {
        // For public access, don't make authenticated API calls
        // Use only the data already available in the alarm object
        if (!isPublicAccess) {
          const locationId = alarm?.Location || alarm?.locationModel?.id;
          if (locationId) {
            try {
              const response = await locationsApi.getById(locationId);
              setLocationDetails(response.data?.data || response.data);
            } catch (err) {
              // If API call fails, just use the locationModel from alarm
              console.warn('Could not load additional location details:', err.message);
            }
          }
        }

        // Build hierarchy from locationTree if available
        if (alarm?.locationTree && Array.isArray(alarm.locationTree)) {
          setLocationHierarchy(alarm.locationTree);
        }
      } catch (err) {
        console.error('Failed to load location details:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLocationDetails();
  }, [alarm, isPublicAccess]);

  // Auto-load floor plans on mount (for navigation panel availability)
  useEffect(() => {
    if (!isPublicAccess && (alarm?.Location || alarm?.locationModel?.id)) {
      loadFloorPlans();
    }
  }, [alarm?.Location, alarm?.locationModel?.id, isPublicAccess, locationHierarchy]);

  // Load floor plans for the location (check parent hierarchy)
  const loadFloorPlans = async () => {
    const locationId = alarm?.Location || alarm?.locationModel?.id;
    if (!locationId) return;

    setLoadingFloorPlans(true);
    try {
      let plans = [];
      let linkedNodeId = alarm?.locationModel?.wayfinding_node_id;
      let linkedFloorPlanId = null;

      // If wayfinding_node_id not in locationModel, try to fetch fresh location data
      if (!linkedNodeId && locationId && !isPublicAccess) {
        try {
          const freshLocation = await locationsApi.getById(locationId);
          const freshData = freshLocation.data?.data || freshLocation.data;
          if (freshData?.wayfinding_node_id) {
            linkedNodeId = freshData.wayfinding_node_id;
          }
        } catch (err) {
          // Silently continue - will try other strategies
        }
      }

      // Strategy 1: If location has a linked wayfinding node, get its floor plan directly
      if (linkedNodeId) {
        try {
          const node = await indoorMappingService.getWayfindingNode(linkedNodeId);

          if (node?.floor_plan_id) {
            linkedFloorPlanId = node.floor_plan_id;
            const floorPlan = await indoorMappingService.getFloorPlan(linkedFloorPlanId);

            if (floorPlan) {
              plans = [floorPlan];
              setLinkedWayfindingNodeId(linkedNodeId); // Store the node ID for FloorPlanMap
            }
          }
        } catch (err) {
          console.warn('[LocationTab] Error loading floor plan via linked node:', err.message);
        }
      }

      // Strategy 2: Try direct location
      if (plans.length === 0) {
        try {
          plans = await indoorMappingService.getFloorPlans(locationId);
        } catch {
          plans = [];
        }
      }

      // Strategy 3: If no plans found, traverse up the hierarchy
      if (plans.length === 0 && locationHierarchy.length > 0) {
        for (const loc of [...locationHierarchy].reverse()) {
          try {
            const parentPlans = await indoorMappingService.getFloorPlans(loc.id);
            if (parentPlans?.length > 0) {
              plans = parentPlans;
              break;
            }
          } catch {
            // Continue to next parent
          }
        }
      }

      // Strategy 4: If still no plans and we have parentLocation, try that directly
      if (plans.length === 0 && alarm?.locationModel?.parentLocation) {
        try {
          plans = await indoorMappingService.getFloorPlans(alarm.locationModel.parentLocation);
        } catch {
          // Ignore
        }
      }

      setFloorPlans(plans || []);

      // Load POIs from all floor plans in the building
      if (plans?.length > 0) {
        loadBuildingPois(plans);
      }

      // Auto-select the floor plan that was found via the node, or first geo-referenced
      if (linkedFloorPlanId && plans?.length > 0) {
        const linkedPlan = plans.find(p => p.id === linkedFloorPlanId);
        if (linkedPlan) {
          setSelectedFloorPlan(linkedPlan);
          return;
        }
      }

      // Fallback: select first geo-referenced plan
      const geoReferencedPlan = plans?.find(p => p.is_geo_referenced);
      if (geoReferencedPlan) {
        setSelectedFloorPlan(geoReferencedPlan);
      } else if (plans?.length > 0) {
        setSelectedFloorPlan(plans[0]);
      }
    } catch (err) {
      console.warn('Could not load floor plans:', err.message);
    } finally {
      setLoadingFloorPlans(false);
    }
  };

  // Load POIs for all floor plans in the building
  const loadBuildingPois = async (plans) => {
    if (!plans || plans.length === 0) return;

    setLoadingPois(true);
    try {
      const allPois = [];
      for (const plan of plans) {
        try {
          const pois = await indoorMappingService.getPOIs(plan.id);
          if (pois?.length > 0) {
            // Add floor plan info to each POI
            const poisWithFloor = pois.map(poi => ({
              ...poi,
              floorPlanName: plan.floor_name || `Floor ${plan.floor_number}`,
              floorPlanId: plan.id,
            }));
            allPois.push(...poisWithFloor);
          }
        } catch (err) {
          console.warn(`Could not load POIs for floor plan ${plan.id}:`, err.message);
        }
      }
      setBuildingPois(allPois);
    } catch (err) {
      console.warn('Could not load building POIs:', err.message);
    } finally {
      setLoadingPois(false);
    }
  };

  // Handle opening map dialog
  const handleOpenMap = async () => {
    setMapDialogOpen(true);
    if (floorPlans.length === 0) {
      await loadFloorPlans();
    }
  };

  // Get best available location info for mobile buttons
  // Priority for mobile buttons:
  //   1) If person's home location matches an antenna signal location -> use home (confirmed)
  //   2) If person has home location but not in antenna list -> use strongest signal (person not at home)
  //   3) Strongest antenna signal if no person location
  // Priority for fixed buttons:
  //   1) Direct location assignment
  //   2) Device location
  const getBestLocation = () => {
    const antennas = alarm?.Content?.antennas || [];

    // Resolve person from multiple sources: direct personModel, or from device
    const person = alarm?.personModel || alarm?.deviceModel?.personModel;
    // Person model uses personLocation field (not locationId)
    const personLocationId = person?.personLocation ||
                             alarm?.Content?.personLocationId;
    // Person's nested locationModel has the location details (from API include)
    const personLocation = person?.locationModel ||
                           (alarm?.Content?.personLocationName ? { friendlyName: alarm.Content.personLocationName, id: alarm.Content.personLocationId } : null);

    // Determine if this is a mobile button
    const isMobileButton = alarm?.Content?.buttonType === 'mobile' ||
                           alarm?.Content?.buttonType === 'emergencyMobile' ||
                           alarm?.deviceModel?.devicePerson ||
                           alarm?.buttonDeviceModel?.devicePerson ||
                           alarm?.personModel?.id ||
                           alarm?.Content?.personId;

    // For MOBILE BUTTONS: Check if person's home location is in antenna signals
    if (isMobileButton && personLocationId && antennas.length > 0) {
      const personAntennaMatch = antennas.find(ant =>
        Number(ant.locationId) === Number(personLocationId) ||
        String(ant.locationId) === String(personLocationId)
      );

      if (personAntennaMatch) {
        // Person's home location confirmed by antenna signal - they are at home
        return {
          source: "Person's Home Location (Confirmed by Signal)",
          sourceDescription: 'The person is at their assigned home location, confirmed by antenna signal.',
          location: personLocation || alarm.locationModel || { id: personLocationId, friendlyName: personAntennaMatch.locationName },
          person: person,
          personHomeLocation: personLocation?.friendlyName || personAntennaMatch.locationName,
          antenna: personAntennaMatch,
          confidence: 'high',
          isAtHome: true
        };
      } else {
        // Person has home location but the signal is from a different location
        // Use the strongest antenna signal - person is NOT at their home location
        const strongest = antennas.reduce((best, ant) =>
          (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);

        if (strongest) {
          return {
            source: 'Strongest Antenna Signal (Person Not at Home)',
            sourceDescription: `The person's home location is "${personLocation?.friendlyName || 'Location ID ' + personLocationId}" but the strongest signal is from a different location.`,
            location: alarm.locationModel || {
              id: strongest.locationId,
              friendlyName: strongest.locationName || (alarm.locationModel?.friendlyName)
            },
            person: person,
            personHomeLocation: personLocation?.friendlyName,
            antenna: strongest,
            confidence: 'medium',
            isAtHome: false
          };
        }
      }
    }

    // For mobile buttons without person location, use strongest antenna signal
    if (isMobileButton && antennas.length > 0) {
      const strongest = antennas.reduce((best, ant) =>
        (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);
      if (strongest?.locationId || strongest?.locationName) {
        return {
          source: 'Strongest Antenna Signal',
          sourceDescription: 'Location determined from the strongest antenna signal. No home location assigned to person.',
          location: alarm.locationModel || {
            id: strongest.locationId,
            friendlyName: strongest.locationName || alarm.locationModel?.friendlyName
          },
          antenna: strongest,
          confidence: 'medium'
        };
      }
    }

    // For mobile buttons with person location but no antenna data
    if (isMobileButton && personLocation) {
      return {
        source: "Person's Assigned Location",
        sourceDescription: 'Using the person\'s assigned home location. No antenna signal data available.',
        location: personLocation,
        person: person,
        personHomeLocation: personLocation?.friendlyName,
        confidence: 'medium',
        isAtHome: true
      };
    }

    // For FIXED BUTTONS or when no mobile button context:
    // Priority 1: Direct location assignment (if set on alarm)
    if (alarm?.locationModel) {
      return {
        source: 'Direct Assignment',
        sourceDescription: 'Location directly assigned to this alarm.',
        location: alarm.locationModel,
        confidence: 'high'
      };
    }

    // Priority 2: Device location (fixed buttons)
    if (alarm?.deviceModel?.locationModel) {
      return {
        source: 'Device Location (Fixed Button)',
        sourceDescription: 'Location of the fixed button device.',
        location: alarm.deviceModel.locationModel,
        confidence: 'high'
      };
    }

    // Fallback: Use antenna data even for fixed buttons if nothing else available
    if (antennas.length > 0) {
      const strongest = antennas.reduce((best, ant) =>
        (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);
      if (strongest?.locationName) {
        return {
          source: 'Strongest Antenna Signal',
          sourceDescription: 'Location determined from antenna signal.',
          location: { id: strongest.locationId, friendlyName: strongest.locationName },
          antenna: strongest,
          confidence: 'medium'
        };
      }
    }

    return null;
  };

  const bestLocation = getBestLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Location Confidence Banner */}
      {bestLocation ? (
        <Alert
          severity={bestLocation.confidence === 'high' ? 'success' :
                   bestLocation.confidence === 'medium' ? 'warning' : 'info'}
          icon={<PreciseLocationIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2">
            Location Source: {bestLocation.source}
          </Typography>
          <Typography variant="body2">
            Confidence: {bestLocation.confidence.toUpperCase()}
            {bestLocation.confidence !== 'high' &&
              ' - Location may be approximate based on signal strength'}
          </Typography>
          {bestLocation.sourceDescription && (
            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              {bestLocation.sourceDescription}
            </Typography>
          )}
          {/* Show strongest antenna info */}
          {bestLocation.antenna && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Strongest antenna signal: {bestLocation.antenna.antennaName || 'Unknown antenna'}
              {bestLocation.antenna.locationName && ` at ${bestLocation.antenna.locationName}`}
              {' '}({bestLocation.antenna.rssiDbm} dBm, {bestLocation.antenna.signalQuality || 'unknown'})
            </Typography>
          )}
          {/* Show if person is not at home */}
          {bestLocation.isAtHome === false && bestLocation.personHomeLocation && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={`Person's home: ${bestLocation.personHomeLocation}`}
              sx={{ mt: 1 }}
            />
          )}
        </Alert>
      ) : (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">No Location Available</Typography>
          <Typography variant="body2">
            Unable to determine location. Possible reasons:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
            {!alarm?.locationModel && !alarm?.Location && (
              <li><Typography variant="caption">No location directly assigned to this alarm</Typography></li>
            )}
            {!alarm?.personModel?.locationModel && !alarm?.Content?.personLocationId && (
              <li><Typography variant="caption">Person has no home location configured</Typography></li>
            )}
            {(!alarm?.Content?.antennas || alarm.Content.antennas.length === 0) && (
              <li><Typography variant="caption">No antenna signal data received from device</Typography></li>
            )}
            {!alarm?.deviceModel?.locationModel && (
              <li><Typography variant="caption">Button device has no location assignment</Typography></li>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            To fix: Configure a location on the device or person in the admin panel.
          </Typography>
        </Alert>
      )}

      {/* Navigation Panel - Show when we have location data */}
      {alarm?.locationModel ? (
        <AlarmNavigationPanel
          alarmLocation={alarm?.locationModel}
          destinationCoords={
            linkedWayfindingNodeId ? null : // Will be fetched from node
            selectedFloorPlan?.is_geo_referenced ? {
              lat: (parseFloat(selectedFloorPlan.bounds_north) + parseFloat(selectedFloorPlan.bounds_south)) / 2,
              lng: (parseFloat(selectedFloorPlan.bounds_east) + parseFloat(selectedFloorPlan.bounds_west)) / 2,
            } : null
          }
          floorPlanId={selectedFloorPlan?.is_geo_referenced ? selectedFloorPlan?.id : null}
          wayfindingNodeId={linkedWayfindingNodeId || alarm?.locationModel?.wayfinding_node_id}
          isLoading={loadingFloorPlans}
        />
      ) : (
        <Alert severity="info" icon={<NavigationIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Navigation Not Available</Typography>
          <Typography variant="body2">
            Navigation requires a location to be associated with this alarm.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Button Type Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeviceIcon color="primary" />
                Button Type
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={isFixedButton ? 'FIXED BUTTON' : isMobileButton ? 'MOBILE BUTTON' : 'UNKNOWN'}
                  color={isFixedButton ? 'success' : isMobileButton ? 'primary' : 'warning'}
                  size="large"
                  sx={{ fontWeight: 'bold' }}
                />
                {buttonType && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Type: {buttonType}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {isFixedButton
                    ? 'This button is permanently installed at a specific location'
                    : isMobileButton
                    ? 'This button is carried by a person and can move between locations'
                    : 'Button type could not be determined'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Primary Location Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="primary" />
                Primary Location
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {alarm?.locationModel || locationDetails ? (
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {(alarm?.locationModel || locationDetails)?.friendlyName || 'Unknown Location'}
                  </Typography>

                  {/* Location hierarchy breadcrumb */}
                  {locationHierarchy.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {locationHierarchy.map((loc, index) => (
                        <React.Fragment key={loc.id || index}>
                          <Chip
                            icon={getLocationTypeIcon(loc.locationType)}
                            label={loc.friendlyName}
                            size="small"
                            variant={index === locationHierarchy.length - 1 ? 'filled' : 'outlined'}
                            color={index === locationHierarchy.length - 1 ? 'primary' : 'default'}
                          />
                          {index < locationHierarchy.length - 1 && (
                            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              &gt;
                            </Typography>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Location ID</Typography>
                      <Typography variant="body1">
                        {alarm?.Location || alarm?.locationModel?.id || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Location Type</Typography>
                      <Typography variant="body1">
                        {alarm?.locationModel?.locationTypeModel?.friendlyName ||
                         locationDetails?.locationTypeModel?.friendlyName ||
                         locationDetails?.locationType?.friendlyName ||
                         '-'}
                      </Typography>
                    </Grid>
                    {(locationDetails?.address || locationDetails?.data?.address) && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Address</Typography>
                        <Typography variant="body1">
                          {locationDetails.address || locationDetails.data?.address}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No location information available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Location Reasoning */}
        {alarm?.locationReasoning && (
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="info" />
                  Location Determination Logic
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {alarm.locationReasoning}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Antenna Information (for mobile buttons or signal-based location) */}
        {alarm?.Content?.antennas && alarm.Content.antennas.length > 0 && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AntennaIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} />
                  Antenna Signals ({alarm.Content.antennas.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Antenna</TableCell>
                        <TableCell align="center">Signal (dBm)</TableCell>
                        <TableCell align="center">Quality</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Hub</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alarm.Content.antennas
                        .sort((a, b) => (b.rssiDbm || -999) - (a.rssiDbm || -999))
                        .map((antenna, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: index === 0 ? 'success.light' : 'inherit',
                              fontWeight: index === 0 ? 'bold' : 'normal'
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AntennaIcon fontSize="small" />
                                {antenna.antennaName || 'Unknown'}
                                {index === 0 && (
                                  <Chip label="Strongest" size="small" color="success" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <SignalIcon
                                  fontSize="small"
                                  color={antenna.rssiDbm >= -60 ? 'success' :
                                         antenna.rssiDbm >= -75 ? 'warning' : 'error'}
                                />
                                {antenna.rssiDbm || 'N/A'}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={antenna.signalQuality || 'Unknown'}
                                size="small"
                                color={antenna.signalQuality === 'excellent' ? 'success' :
                                       antenna.signalQuality === 'good' ? 'primary' :
                                       antenna.signalQuality === 'fair' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {antenna.locationName || (antenna.locationId ? `ID: ${antenna.locationId}` : '-')}
                            </TableCell>
                            <TableCell>{antenna.hubHostname || '-'}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Person Information (for mobile buttons) */}
        {(isMobileButton || alarm?.personModel || alarm?.Content?.personId || alarm?.deviceModel?.devicePerson || alarm?.buttonDeviceModel?.devicePerson) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Person Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {(() => {
                  // Resolve person info from multiple sources
                  const person = alarm?.personModel || alarm?.deviceModel?.personModel;
                  const personName = person?.friendlyName ||
                                     alarm?.Content?.personName;
                  const personId = person?.id ||
                                   alarm?.Content?.personId ||
                                   alarm?.deviceModel?.devicePerson ||
                                   alarm?.buttonDeviceModel?.devicePerson;
                  const personHomeLocation = person?.locationModel?.friendlyName ||
                                             alarm?.Content?.personLocationName;
                  const isAtHome = bestLocation?.isAtHome;

                  if (personName || personId) {
                    return (
                      <List dense>
                        <ListItem>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText
                            primary="Name"
                            secondary={personName || `Person ID: ${personId}`}
                          />
                        </ListItem>
                        {personHomeLocation && (
                          <ListItem>
                            <ListItemIcon><LocationIcon color={isAtHome ? 'success' : 'action'} /></ListItemIcon>
                            <ListItemText
                              primary="Home Location"
                              secondary={
                                <Box component="span">
                                  {personHomeLocation}
                                  {isAtHome !== undefined && (
                                    <Chip
                                      size="small"
                                      label={isAtHome ? 'Currently here' : 'Not currently here'}
                                      color={isAtHome ? 'success' : 'warning'}
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        )}
                        {person?.unit && (
                          <ListItem>
                            <ListItemIcon><RoomIcon /></ListItemIcon>
                            <ListItemText
                              primary="Unit/Room"
                              secondary={person.unit}
                            />
                          </ListItem>
                        )}
                        {!personName && personId && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              Person name not available. Button is linked to Person ID: {personId}.
                            </Typography>
                          </Alert>
                        )}
                      </List>
                    );
                  } else {
                    return (
                      <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Person data not available
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Possible reasons:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                          <li><Typography variant="caption">Button device not linked to a person</Typography></li>
                          <li><Typography variant="caption">Person record has no friendly name set</Typography></li>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          To link a person: Go to Devices &gt; Select this device &gt; Assign Person
                        </Typography>
                      </Alert>
                    );
                  }
                })()}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Interactive Map Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon color="primary" />
                  Floor Plan / Map
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<NavigationIcon />}
                  onClick={handleOpenMap}
                  disabled={isPublicAccess}
                >
                  Show on Floor Plan
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Alert severity="info" icon={<FloorIcon />}>
                <Typography variant="body2">
                  Click "Show on Floor Plan" to view the alarm location on an interactive map.
                  {floorPlans.length > 0 && ` ${floorPlans.length} floor plan(s) available.`}
                </Typography>
              </Alert>

              {alarm?.locationModel?.coordinates && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  GPS Coordinates: {alarm.locationModel.coordinates.lat}, {alarm.locationModel.coordinates.lng}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Floor Plan Map Dialog */}
      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon color="primary" />
            Alarm Location on Floor Plan
          </Box>
          {floorPlans.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Floor</InputLabel>
              <Select
                value={selectedFloorPlan?.id || ''}
                onChange={(e) => {
                  const plan = floorPlans.find(p => p.id === e.target.value);
                  setSelectedFloorPlan(plan);
                }}
                label="Floor"
              >
                {floorPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.floor_name || `Floor ${plan.floor_number}`}
                    {plan.is_geo_referenced && ' ✓'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingFloorPlans ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : selectedFloorPlan?.is_geo_referenced ? (
            <Grid container spacing={2}>
              {/* Map */}
              <Grid item xs={12} md={buildingPois.length > 0 ? 8 : 12}>
                <Box sx={{ height: { xs: 350, sm: 450, md: 500 }, width: '100%' }}>
                  <FloorPlanMap
                    floorPlan={selectedFloorPlan}
                    alarmLocation={alarm?.locationModel}
                    wayfindingNodeId={linkedWayfindingNodeId || alarm?.locationModel?.wayfinding_node_id}
                  />
                </Box>
              </Grid>

              {/* POI List Panel */}
              {buildingPois.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ height: { xs: 250, md: 500 }, overflow: 'auto' }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Points of Interest ({buildingPois.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click on a POI to see it on the map
                      </Typography>
                    </Box>
                    {loadingPois ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <List dense>
                        {buildingPois.map((poi) => {
                          const poiTypeColor = getPOIColor(poi);
                          const poiTypeName = poi.poiType?.name || 'POI';
                          const isOnCurrentFloor = poi.floorPlanId === selectedFloorPlan?.id;

                          return (
                            <ListItem
                              key={poi.id}
                              sx={{
                                borderLeft: 3,
                                borderColor: poiTypeColor,
                                opacity: isOnCurrentFloor ? 1 : 0.6,
                                bgcolor: isOnCurrentFloor ? 'action.hover' : 'transparent',
                              }}
                              secondaryAction={
                                !isOnCurrentFloor && (
                                  <Chip
                                    label={poi.floorPlanName}
                                    size="small"
                                    variant="outlined"
                                  />
                                )
                              }
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: poiTypeColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
                                    {poiTypeName.charAt(0).toUpperCase()}
                                  </Typography>
                                </Box>
                              </ListItemIcon>
                              <ListItemText
                                primary={poi.name}
                                secondary={poiTypeName}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: isOnCurrentFloor ? 'bold' : 'normal' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : selectedFloorPlan ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This floor plan has not been geo-referenced yet. The alarm location cannot be displayed accurately.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                An administrator needs to geo-reference this floor plan in the Indoor Mapping settings.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info">
              <Typography variant="body2">
                No floor plans available for this location.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Floor plans can be uploaded in the Indoor Mapping administration section.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Floor Plan Map Component - displays floor plan with alarm marker, POIs, and navigation path
 */
const FloorPlanMap = ({ floorPlan, alarmLocation, wayfindingNodeId }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const [markerPosition, setMarkerPosition] = useState(null);
  const [nodeInfo, setNodeInfo] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pois, setPois] = useState([]);
  const [pathNodes, setPathNodes] = useState([]); // Nodes for the path to entrance
  const [entranceNode, setEntranceNode] = useState(null);

  // Get floor plan image URL
  const imageUrl = floorPlan?.image_url || floorPlan?.imageUrl;
  const fullImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`)
    : null;

  // Calculate bounds from floor plan - memoized to prevent re-renders
  const bounds = React.useMemo(() => {
    if (floorPlan?.bounds_north && floorPlan?.bounds_south && floorPlan?.bounds_east && floorPlan?.bounds_west) {
      return [
        [parseFloat(floorPlan.bounds_south), parseFloat(floorPlan.bounds_west)],
        [parseFloat(floorPlan.bounds_north), parseFloat(floorPlan.bounds_east)],
      ];
    }
    return null;
  }, [floorPlan?.bounds_north, floorPlan?.bounds_south, floorPlan?.bounds_east, floorPlan?.bounds_west]);

  // Memoize center to prevent infinite re-renders
  const center = React.useMemo(() => {
    if (bounds) {
      return [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
    }
    return [50.9847, 3.5278]; // Default center
  }, [bounds]);

  // Load all map data: node position, POIs, and path
  useEffect(() => {
    if (hasLoaded) return;

    const loadMapData = async () => {
      if (!floorPlan?.id) {
        setMarkerPosition(center);
        setHasLoaded(true);
        return;
      }

      setLoadingData(true);
      try {
        // Load POIs
        const poisData = await indoorMappingService.getPOIs(floorPlan.id);
        setPois(poisData || []);

        // Load wayfinding graph
        const graph = await indoorMappingService.getWayfindingGraph(floorPlan.id);
        const nodes = graph.nodes || [];
        const edges = graph.edges || [];

        // Find the alarm location node
        let alarmNode = null;
        if (wayfindingNodeId) {
          alarmNode = nodes.find(n => Number(n.id) === Number(wayfindingNodeId));
        }

        if (alarmNode) {
          // ONLY use geo coordinates - x_position/y_position are pixel coords, not useful on a geo-referenced map
          const nodeLat = alarmNode.geo_lat;
          const nodeLng = alarmNode.geo_lng;

          console.log('[FloorPlanMap] Alarm node:', alarmNode);
          console.log('[FloorPlanMap] geo_lat:', nodeLat, 'geo_lng:', nodeLng);

          if (nodeLat && nodeLng) {
            setMarkerPosition([parseFloat(nodeLat), parseFloat(nodeLng)]);
            setNodeInfo(alarmNode);
          } else {
            console.warn('[FloorPlanMap] Node has no geo coordinates, using center');
            setMarkerPosition(center);
          }

          // Find entrance node (look for node_type 'entrance' or a POI of type entrance)
          const entrancePoi = poisData?.find(p =>
            p.poiType?.code === 'entrance' ||
            p.poiType?.name?.toLowerCase().includes('entrance') ||
            p.poi_type_id === 7 // Entrance type ID
          );

          // Find a node near the entrance POI or a node marked as entrance
          let entranceN = nodes.find(n => n.node_type === 'entrance' && n.geo_lat && n.geo_lng);
          if (!entranceN && entrancePoi) {
            // Find closest node to entrance POI
            const poiLat = entrancePoi.geo_lat;
            const poiLng = entrancePoi.geo_lng;
            if (poiLat && poiLng) {
              let minDist = Infinity;
              nodes.forEach(n => {
                const nLat = n.geo_lat;
                const nLng = n.geo_lng;
                if (nLat && nLng) {
                  const dist = Math.sqrt(Math.pow(nLat - poiLat, 2) + Math.pow(nLng - poiLng, 2));
                  if (dist < minDist) {
                    minDist = dist;
                    entranceN = n;
                  }
                }
              });
            }
          }

          // Use first node WITH geo coordinates as fallback entrance
          if (!entranceN) {
            entranceN = nodes.find(n => n.geo_lat && n.geo_lng);
          }

          if (entranceN) {
            setEntranceNode(entranceN);
            // Calculate path from alarm node to entrance using simple BFS
            const path = findPath(alarmNode.id, entranceN.id, nodes, edges);
            setPathNodes(path);
          }
        } else {
          setMarkerPosition(center);
        }
      } catch (err) {
        console.error('[FloorPlanMap] Error loading map data:', err);
        setMarkerPosition(center);
      } finally {
        setLoadingData(false);
        setHasLoaded(true);
      }
    };

    loadMapData();
  }, [wayfindingNodeId, floorPlan?.id, center, hasLoaded]);

  // Simple BFS pathfinding
  const findPath = (startId, endId, nodes, edges) => {
    if (startId === endId) return [nodes.find(n => n.id === startId)];

    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const adjacency = new Map();

    // Build adjacency list
    nodes.forEach(n => adjacency.set(n.id, []));
    edges.forEach(e => {
      const fromId = e.from_node_id ?? e.fromNodeId;
      const toId = e.to_node_id ?? e.toNodeId;
      if (adjacency.has(fromId)) adjacency.get(fromId).push(toId);
      if (adjacency.has(toId)) adjacency.get(toId).push(fromId); // bidirectional
    });

    // BFS
    const queue = [[startId]];
    const visited = new Set([startId]);

    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];

      if (current === endId) {
        return path.map(id => nodeMap.get(id)).filter(Boolean);
      }

      const neighbors = adjacency.get(current) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push([...path, neighbor]);
        }
      }
    }

    return []; // No path found
  };

  // Get coordinates for a node - ONLY use geo coordinates
  const getNodeCoords = (node) => {
    const lat = node.geo_lat;
    const lng = node.geo_lng;
    return lat && lng ? [parseFloat(lat), parseFloat(lng)] : null;
  };

  // Create POI markers
  const createPOIIcon = (type, color = '#2196F3') => {
    return new L.DivIcon({
      className: 'poi-marker',
      html: `<div style="
        width: 24px;
        height: 24px;
        background: ${color};
        border: 2px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: white;
        font-weight: bold;
      ">${type?.charAt(0)?.toUpperCase() || 'P'}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    });
  };

  // Get POI color based on type
  const getPOIColor = (poi) => {
    const typeName = poi.poiType?.code || poi.poiType?.name || '';
    const colorMap = {
      entrance: '#4CAF50',
      exit: '#F44336',
      elevator: '#009688',
      stairs: '#607D8B',
      restroom: '#9C27B0',
      medical: '#E91E63',
      office: '#2196F3',
      restaurant: '#FF9800',
    };
    return colorMap[typeName.toLowerCase()] || '#2196F3';
  };

  if (!bounds || !fullImageUrl) {
    return (
      <Alert severity="error">
        Floor plan data is incomplete. Missing bounds or image URL.
      </Alert>
    );
  }

  if (loadingData || !markerPosition) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Create path polyline positions
  const pathPositions = pathNodes
    .map(getNodeCoords)
    .filter(Boolean);

  return (
    <MapContainer
      center={markerPosition}
      zoom={20}
      style={{ width: '100%', height: '100%' }}
      zoomControl={true}
    >
      <ImageOverlay
        url={fullImageUrl}
        bounds={bounds}
        opacity={1}
        zIndex={1}
      />

      {/* Navigation Path */}
      {pathPositions.length > 1 && (
        <Polyline
          positions={pathPositions}
          pathOptions={{
            color: '#2196F3',
            weight: 4,
            opacity: 0.8,
            dashArray: '10, 10',
          }}
        />
      )}

      {/* POI Markers - only show POIs with geo coordinates */}
      {pois.map((poi) => {
        const poiLat = poi.geo_lat;
        const poiLng = poi.geo_lng;
        if (!poiLat || !poiLng) return null;

        const color = getPOIColor(poi);
        const typeName = poi.poiType?.name || 'POI';

        return (
          <Marker
            key={poi.id}
            position={[parseFloat(poiLat), parseFloat(poiLng)]}
            icon={createPOIIcon(typeName, color)}
          >
            <Popup>
              <Typography variant="subtitle2">{poi.name}</Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {typeName}
              </Typography>
              {poi.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {poi.description}
                </Typography>
              )}
            </Popup>
          </Marker>
        );
      })}

      {/* Entrance Marker */}
      {entranceNode && getNodeCoords(entranceNode) && (
        <Marker
          position={getNodeCoords(entranceNode)}
          icon={new L.DivIcon({
            className: 'entrance-marker',
            html: `<div style="
              width: 30px;
              height: 30px;
              background: #4CAF50;
              border: 3px solid #fff;
              border-radius: 50%;
              box-shadow: 0 2px 6px rgba(0,0,0,0.4);
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M19 19V5c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v14H3v2h18v-2h-2zm-8 0H9V5h2v14zm4 0h-2V5h2v14z"/>
              </svg>
            </div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
            popupAnchor: [0, -15],
          })}
        >
          <Popup>
            <Typography variant="subtitle2" color="success.main">
              ENTRANCE
            </Typography>
            <Typography variant="body2">
              Start your route here
            </Typography>
          </Popup>
        </Marker>
      )}

      {/* Alarm Location Marker (on top) */}
      <Marker position={markerPosition} icon={alarmIcon}>
        <Popup>
          <Typography variant="subtitle2" color="error">
            ALARM LOCATION
          </Typography>
          <Typography variant="body2">
            {alarmLocation?.friendlyName || 'Location'}
          </Typography>
          {nodeInfo && (
            <Typography variant="caption" display="block" color="text.secondary">
              Node: {nodeInfo.node_type || 'waypoint'} (ID: {nodeInfo.id})
            </Typography>
          )}
          {pathNodes.length > 1 && (
            <Typography variant="caption" display="block" color="primary">
              {pathNodes.length - 1} steps from entrance
            </Typography>
          )}
          {!wayfindingNodeId && (
            <Typography variant="caption" display="block" color="warning.main">
              Position approximate - no linked node
            </Typography>
          )}
        </Popup>
      </Marker>
    </MapContainer>
  );
};

export default LocationTab;
